import { Fragment, useState } from 'react';
import { getDeal, postCloseDeal, postVoidEvent } from '../api/endpoints';
import { useApi } from '../api/hooks';
import type { DealDetail, EventRow, FlowColumn, FlowNodeView, PositionRow, Receivable } from '../api/model';
import { ApiError } from '../api/client';
import { Async, ErrorNotice } from '../components/Async';
import { CardHead, Kpi } from '../components/Card';
import { AssignDealDialog } from '../components/AssignDealDialog';
import { EventsTable } from '../components/EventsTable';
import { HelpLabel } from '../components/Help';
import { PrehistoryCreditNote } from '../components/PrehistoryCreditNote';
import { PriceSourceLabel } from '../components/PriceSource';
import { Table, type Column } from '../components/Table';
import { Val, signClass } from '../components/Val';
import { formatDecimal, formatPercent } from '../lib/decimal';
import { Link } from '../lib/router';
import { formatUtc } from '../lib/time';

const COLUMN_ORDER: FlowColumn[] = ['loan', 'conversion', 'location', 'repayment'];

function FlowNodeBox({ n }: { n: FlowNodeView }) {
  const cls = `node${n.emphasis === 'loan' ? ' loan' : n.emphasis === 'out' ? ' out' : n.emphasis === 'dashed' ? ' dashed' : ''}`;
  const labelColor = n.emphasis === 'loan' ? 'var(--accent)' : n.emphasis === 'out' ? 'var(--warn)' : undefined;
  return (
    <div className={cls} data-node={n.id}>
      {n.label ? <span className="label" style={{ color: labelColor }}>{n.label}</span> : null}
      <span className="mono" style={{ fontWeight: 600, fontSize: n.emphasis === 'loan' ? 18 : undefined }}>{n.title}</span>
      {n.detail ? <span className="mono muted small">{n.detail}</span> : null}
      {n.note ? <span className="muted small">{n.note}</span> : null}
    </div>
  );
}

/** Loan -> conversions -> locations -> repayment, built from HTML boxes. */
export function FlowDiagram({ flow }: { flow: DealDetail['flow'] }) {
  const cols = COLUMN_ORDER.map((c) => ({ c, nodes: flow.filter((n) => n.column === c) })).filter((x) => x.nodes.length > 0);
  if (cols.length === 0) return <div className="muted">Még nincs pénzáramlás ebben az ügyletben.</div>;
  return (
    <div className="table-wrap">
      <div className="flow" role="group" aria-label="Pénzáramlás diagram">
        {cols.map((col, i) => (
          <Fragment key={col.c}>
            {i > 0 ? <div className="arrow" aria-hidden="true">→</div> : null}
            <div className="flow-col" data-column={col.c} style={col.c === 'conversion' || col.c === 'location' ? { flexBasis: 250 } : undefined}>
              {col.nodes.map((n) => <FlowNodeBox key={n.id} n={n} />)}
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

const positionColumns: Column<PositionRow>[] = [
  { key: 'asset', label: 'Coin', sortValue: (p) => p.asset, render: (p) => <b>{p.asset}</b> },
  { key: 'loc', label: 'Helyszín', sortValue: (p) => p.location, render: (p) => p.location },
  { key: 'qty', label: 'Mennyiség', numeric: true, help: 'quantity', ariaLabel: 'mennyiség', sortValue: (p) => p.quantity, render: (p) => <span className="mono">{formatDecimal(p.quantity)}</span> },
  { key: 'avg', label: 'Átlagár (D11)', numeric: true, help: 'avgCost', ariaLabel: 'átlagár', sortValue: (p) => p.average_cost, render: (p) => <span className="mono"><Val value={p.average_cost} reason={p.average_cost_reason} dp={2} unit={p.average_cost_quote} /></span> },
  { key: 'cost', label: 'Bekerülési érték, USD', numeric: true, help: 'costValue', ariaLabel: 'bekerülési érték', sortValue: (p) => p.cost_value_usd, render: (p) => <span className="mono"><Val value={p.cost_value_usd} reason={p.cost_reason} dp={2} /></span> },
  { key: 'val', label: 'Mai érték, USD', numeric: true, help: 'valueUsd', ariaLabel: 'mai érték', sortValue: (p) => p.value_usd, render: (p) => <span className="mono"><Val value={p.value_usd} reason={p.value_reason} dp={2} /></span> },
  { key: 'unr', label: 'Nem realizált, USD', numeric: true, help: 'unrealizedPos', ariaLabel: 'nem realizált eredmény', sortValue: (p) => p.unrealized_usd, render: (p) => <span className={`mono ${signClass(p.unrealized_usd)}`}><Val value={p.unrealized_usd} reason={p.unrealized_reason} dp={2} sign /></span> },
];

function ReceivableProgress({ r }: { r: Receivable }) {
  const pct = Math.max(0, Math.min(100, Number(r.returned_pct)));
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="card-head">
        <b>{r.location}</b>
        <span className="mono">{formatDecimal(r.returned)} / {formatDecimal(r.sent)} {r.asset}</span>
      </div>
      <div className="progress" role="img" aria-label={`Visszahozva ${r.returned_pct} százalék`}>
        <div style={{ width: `${pct}%` }} />
      </div>
      <span className="small muted">
        Fedezetlen: <b className="mono"><Val value={r.uncovered} reason={r.uncovered_reason} unit={r.asset} dp={2} /></b>
      </span>
    </div>
  );
}

function DealBody({ d, reload }: { d: DealDetail; reload: () => void }) {
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmVoid, setConfirmVoid] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<EventRow | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);
  const loansRemain = actionError instanceof ApiError && actionError.reason === 'open_loans_remain';
  const { deal, kpis } = d;

  const run = async (fn: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await fn();
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e : new Error(String(e)));
    }
  };

  const voidCell = (e: EventRow) => {
    if (e.voided) return <span className="pill">Visszavonva</span>;
    if (confirmVoid === e.id) {
      return (
        <span className="row-wrap">
          <button type="button" className="btn small" onClick={() => { setConfirmVoid(null); void run(() => postVoidEvent(e.id)); }}>Igen, visszavonom</button>
          <button type="button" className="btn small" onClick={() => setConfirmVoid(null)}>Mégse</button>
        </span>
      );
    }
    return <button type="button" className="btn small" onClick={() => setConfirmVoid(e.id)} aria-label={`Esemény visszavonása: ${formatUtc(e.occurred_at)}`}>Visszavonás</button>;
  };

  const flowTitle = (
    <HelpLabel helpKey="flow" label="pénzáramlás" className="" as="h2">
      Pénzáramlás
    </HelpLabel>
  );

  return (
    <>
      <div className="page-head">
        <div className="titles" style={{ gap: 6 }}>
          <Link to="/deals" className="small">← Ügyletek</Link>
          <div className="row-wrap" style={{ alignItems: 'center', gap: 12 }}>
            <h1>#{deal.id} {deal.name}</h1>
            <span className={`pill${deal.status === 'open' ? ' open' : ''}`}>{deal.status === 'open' ? 'Nyitott' : 'Lezárt'}</span>
            {deal.reference_asset ? <span className="pill">ref. coin: {deal.reference_asset}</span> : null}
            {deal.tags.map((t) => <span key={t} className="pill">{t}</span>)}
          </div>
          <div className="muted">
            Megnyitva {formatUtc(deal.opened_at)} UTC{deal.closed_at ? ` · lezárva ${formatUtc(deal.closed_at)} UTC` : ''} · minden, ami ebből a hitelből származik, automatikusan az ügylet része (D26)
          </div>
        </div>
        <div className="row-wrap">
          <Link className="btn" to={`/entries/new?deal=${deal.id}`}>Kézi tétel hozzáadása</Link>
          <Link className="btn" to="/mark">Binance-esemény hozzáadása</Link>
          {deal.status === 'open' ? (
            confirmClose ? (
              <>
                <button type="button" className="btn primary" onClick={() => { setConfirmClose(false); void run(() => postCloseDeal(deal.id)); }}>Igen, lezárom</button>
                <button type="button" className="btn" onClick={() => setConfirmClose(false)}>Mégse</button>
              </>
            ) : (
              <button type="button" className="btn" onClick={() => setConfirmClose(true)}>Ügylet lezárása</button>
            )
          ) : null}
        </div>
      </div>
      {actionError ? <ErrorNotice error={actionError} /> : null}
      {loansRemain ? (
        <div className="row-wrap">
          <button type="button" className="btn" onClick={() => void run(() => postCloseDeal(deal.id, true))}>
            Lezárás nyitott hitellel is
          </button>
        </div>
      ) : null}

      <section aria-label="Ügylet mutatói" className="kpis compact">
        <Kpi compact helpKey="dealLoan" label="Nyitott hitel" value={kpis.open_loan.value} reason={kpis.open_loan.reason} unit="USD" dp={0} caption={kpis.open_loan_caption} />
        <Kpi compact helpKey="priceLevel" label="Felvételi árszint" value={kpis.price.entry} reason={kpis.price.reason} unit={kpis.price.entry_asset} dp={null}
          caption={kpis.price.today !== null ? <>ma {formatDecimal(kpis.price.today)}<PriceSourceLabel source={kpis.price.today_source} />{kpis.price.deviation_pct !== null ? <> · <span className="pos">{formatPercent(kpis.price.deviation_pct)}</span></> : null}</> : undefined} />
        <Kpi compact helpKey="outsideDeal" label="Kint, visszahozandó" tone="warn" value={kpis.outside.value} reason={kpis.outside.reason} unit="USD" dp={0} caption={kpis.outside.breakdown} />
        <Kpi compact helpKey="dealInterest" label="Számolt kamat" value={kpis.interest.value} reason={kpis.interest.reason} unit="USD" />
        <Kpi compact helpKey={kpis.result.kind === 'unrealized' ? 'unrealized' : 'realized'} label={kpis.result.kind === 'unrealized' ? 'Nem realizált eredmény' : 'Realizált eredmény'} tone="result" sign value={kpis.result.value} reason={kpis.result.reason} unit="USD" caption={kpis.result.kind === 'unrealized' ? 'ha ma rendeznénk · kamattal, díjjal' : undefined} />
      </section>

      <PrehistoryCreditNote credits={kpis.open_loan_prehistory_credit} />

      <section className="card" aria-label="Pénzáramlás" style={{ gap: 16 }}>
        <div className="card-head">
          {flowTitle}
          <span className="small muted">hitel → konverzió → helyszín · mennyiség, bekerülési ár</span>
        </div>
        <FlowDiagram flow={d.flow} />
      </section>

      <div className="cols">
        <section className="card grow tight" aria-label="Pozíciók">
          <CardHead title="Pozíciók az ügyletben" />
          <Table columns={positionColumns} rows={d.positions} rowKey={(p) => `${p.asset}-${p.location}`} defaultSort={{ key: 'val', dir: 'desc' }} label="Pozíciók" empty="Az ügyletben nincs nyitott pozíció." />
        </section>
        <section className="card side" aria-label="Visszahozás állapota" style={{ gap: 14 }}>
          <HelpLabel helpKey="receivables" label="visszahozás állapota" className="" as="h2">Visszahozás állapota</HelpLabel>
          {d.receivables.length === 0 ? <div className="muted">Nincs visszahozandó tétel.</div> : d.receivables.map((r) => <ReceivableProgress key={`${r.location}-${r.asset}`} r={r} />)}
          <Link className="btn" to={`/entries/new?type=in&deal=${deal.id}`}>Visszautalás rögzítése</Link>
        </section>
      </div>

      <section className="card tight" aria-label="Események és allokáció">
        <CardHead title="Események és allokáció" hint="Egy esemény mennyisége több ügylet közt felosztható (D12)" />
        <EventsTable events={d.events} variant="deal" onVoid={voidCell} onAssign={setAssigning} label="Események és allokáció" />
      </section>
      {assigning ? <AssignDealDialog event={assigning} onClose={() => setAssigning(null)} onSaved={() => { setAssigning(null); reload(); }} /> : null}
    </>
  );
}

export function DealPage({ id }: { id: number }) {
  const state = useApi((s) => getDeal(id, s), [id]);
  return (
    <main className="page">
      <Async state={state}>{(d) => <DealBody d={d} reload={state.reload} />}</Async>
    </main>
  );
}
